import {
  useGetAllAreaTableListQuery,
  useGetAllTableGroupListQuery,
} from '@api/pos/pos-bo-table-management.api.rtk';
import { useUpdateTransactionMutation } from '@api/pos/pos-transaction-api.rtk';
import { getColor } from '@assets/theme';
import DrawerWrapper from '@components/drawer/drawer-wrapper';
import { NoRowsOverlay } from '@components/table/datatable';
import useAppContext from '@hooks/useAppContext';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import useDisclosure from '@hooks/useDisclosure';
import useToast from '@hooks/useToast';
import POSConfirmationDialog from '@modules/point-of-sale/components/pos-selector/pos-confirmation-dialog';
import {
  selectPOSisHaveOrders,
  selectPOSServiceOptions,
  selectPOSstationID,
  selectPOSTableAreas,
  selectPOSTableDetails,
  selectPOSTableList,
  selectPOSTableOrders,
  selectPOSTableTransactionID,
} from '@modules/point-of-sale/slice/pos-selector';
import {
  removeSelectedTable,
  setSelectedTable,
  TableList,
} from '@modules/point-of-sale/slice/pos-table-area.slice';
import { ArrowForward, Edit, PersonPin } from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { hashSignature } from '@utils/security';
import { useEffect, useMemo, useState } from 'react';
import { usePOSWindowDrawerCtx } from '../pos-order-taking-window';

const POSSelectTableAreaDrawer = () => {
  // #region STATE
  const { openSecCodeDialog } = useAppContext();
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [initialTable, setInitialTable] = useState<TableList>();
  const [showDialog, setShowDialog] = useState(false);
  const [isForSetTable, setIsForSetTable] = useState<boolean>();

  const dialogDisclosure = useDisclosure();

  const {
    borderColor,
    textSecondary,
    info,
    success,
    danger,
    secondary,
    primary,
    white,
    lightGray,
  } = getColor();

  const nationalityList = useAppSelector((state) => state.nationalityList);
  const serviceOptions = useAppSelector(selectPOSServiceOptions);
  const stationID = useAppSelector(selectPOSstationID);
  const selectedPropertyID = useAppSelector(selectPropertyID);
  const areas = useAppSelector(selectPOSTableAreas);
  const tableList = useAppSelector(selectPOSTableList);
  const selectedTransactionId = useAppSelector(selectPOSTableTransactionID);
  const tableDetails = useAppSelector(selectPOSTableDetails);
  const isHaveOrders = useAppSelector(selectPOSisHaveOrders);
  const orders = useAppSelector(selectPOSTableOrders);

  const isHaveNewItems = useMemo(
    () => orders?.some((item) => !Boolean(item.isPrintedOS) && !item.orderID),
    [orders],
  );

  const {
    tableDrawerDisclosure: { isOpen, onClose },
    tableDetailsDrawerDisclosure,
    tableTransferDisclosure,
    autoSelectTableState,
    othersDrawerDisclosure,
  } = usePOSWindowDrawerCtx();

  const [autoSelectTable, setAutoSelectTable] = autoSelectTableState;

  const isForTransferTable = useMemo(
    () => tableTransferDisclosure.isOpen,
    [tableTransferDisclosure.isOpen],
  );

  useEffect(() => {
    if (selectedCategory !== '' || areas?.length <= 0) return;

    const firstCategory = areas?.[0];
    if (firstCategory?.tableAreaID) {
      setSelectedCategory(firstCategory.tableAreaID);
    }
  }, [selectedCategory, areas]);

  useEffect(() => {
    if (!autoSelectTable) return;
    const item = tableList?.find((item) =>
      item.transactionDetails?.some(
        (detail) => detail.transaction_id === autoSelectTable,
      ),
    );

    if (item) {
      dispatch(setSelectedTable(item));
      setAutoSelectTable('');
      setTimeout(() => handleDrawerClose(), 100);
    }
  }, [autoSelectTable, tableList]);

  // #endregion

  // #region API
  const skipQuery =
    selectedPropertyID === 'All Properties' || !isOpen || !selectedCategory;

  const { refetch, isUninitialized } = useGetAllTableGroupListQuery(stationID, {
    skip: !stationID,
    refetchOnMountOrArgChange: true,
  });

  const { isLoading, isFetching } = useGetAllAreaTableListQuery(
    selectedCategory,
    {
      skip: skipQuery,
    },
  );

  const [updateTransaction] = useUpdateTransactionMutation();

  useEffect(() => {
    if (stationID && !isUninitialized) {
      refetch();
    }
  }, [selectedPropertyID, stationID, isUninitialized, refetch]);

  // #endregion

  //#region FUNCTION
  const handleSelectCategory = (category: string) => {
    if (category === selectedCategory) return;
    setSelectedCategory(category);
  };

  const onTableCardClick = (item: TableList, setTable?: boolean) => {
    if (isHaveNewItems && !isForTransferTable) {
      dialogDisclosure.onOpen();
      setInitialTable(item);
      setIsForSetTable(setTable);
      return;
    }

    if (setTable && !isForTransferTable) {
      dispatch(setSelectedTable(item));
      handleDrawerClose();
      return;
    }

    setInitialTable(item);
    setShowDialog(true);
  };

  const onDialogClose = () => {
    setInitialTable(undefined);
    setShowDialog(false);
  };

  const onDialogConfirm = async () => {
    try {
      if (isForTransferTable) {
        await openSecCodeDialog(async (res) => {
          if (!res?.data.isValid) return;

          if (!initialTable) {
            toast({ message: 'No initial table found' });
            return;
          }

          if (!tableDetails) {
            toast({ message: 'No table details found' });
            return;
          }

          const {
            serviceType,
            nationality,
            vatType,
            totalPax,
            guestName,
            remarks,
            transactionID,
            serviceCharge,
          } = tableDetails;

          const tranID = transactionID;

          const selectedServiceOption = serviceOptions.find(
            (opt) => opt.description === serviceType,
          );

          const serviceOptionID = selectedServiceOption
            ? selectedServiceOption._id
            : 1;
          const nationalityID =
            nationalityList.find((item) => item?.code === nationality)?._id ||
            1;

          const signature = hashSignature(
            [
              selectedPropertyID, // propertyID
              stationID, // stationID
              initialTable.tableRefID, // tableID
              Number(serviceCharge), // modifyServiceChargePercentage -> serviceCharge
              'null', // modifyLocalTaxPercentage
              vatType ?? 'null', // modifyVatType
              totalPax, // totalPax
              serviceOptionID, // serviceOptionID
              guestName, // alternateGuestName
              nationalityID, // nationalityID
              remarks ?? 'null', // remarksNote
              'null', // representationExpenseID
            ].join(''),
          );

          const baseTransaction = {
            stationID,
            tableID: initialTable.tableRefID,
            modifyServiceChargePercentage: Number(serviceCharge),
            modifyLocalTaxPercentage: null,
            modifyVatType: vatType,
            totalPax: Number(totalPax),
            serviceOptionID,
            alternateGuestName: guestName,
            remarksNote: remarks,
            representationExpenseID: null,
            nationalityID,
            signature,
          };

          await updateTransaction({
            payload: {
              properties: [
                {
                  property: {
                    propertyID: selectedPropertyID,
                    posTransactions: [
                      {
                        posTransaction: {
                          ...baseTransaction,
                        },
                      },
                    ],
                  },
                },
              ],
            },
            refId: selectedTransactionId,
          })
            .unwrap()
            .then(() => {
              dispatch(removeSelectedTable(tranID));

              handleDrawerClose();
              othersDrawerDisclosure.onClose();
              toast({
                severity: 'success',
                message: 'Table transfer completed successfully',
              });
            });
        }, 'allow_transfer_table');

        return;
      }
      initialTable && dispatch(setSelectedTable(initialTable));
      tableDetailsDrawerDisclosure.onOpen();
    } catch (error) {
      toast({ message: error, severity: 'warning' });
    } finally {
      onDialogClose();
    }
  };

  const onEditClick = (item: TableList) => {
    dispatch(setSelectedTable(item));
    tableDetailsDrawerDisclosure.onOpen();
  };

  const handleDrawerClose = () => {
    onClose();
    tableTransferDisclosure.onClose();
  };
  //#endregion

  return (
    <>
      <DrawerWrapper
        {...{
          open: isOpen,
          onClose: handleDrawerClose,
          withHeader: {
            title: tableDetails
              ? `Selected: ${tableDetails?.tableAcr}`
              : 'Select Table',
            learnMore: () => {},
            sx: {
              maxWidth: '800px',
              minWidth: { xs: '100%', sm: '600px', md: '800px' },
            },
          },
        }}
      >
        {/* CONTENT HEADER*/}
        <Stack
          direction='row'
          sx={{
            p: 2,
            gap: 1,
            minHeight: '116px',
            overflowX: 'auto',
            alignItems: 'center',
          }}
        >
          {areas?.map((category, i) => {
            const [details, number] = category.tableAreaDesc
              .split(/(\d.*)/)
              .filter(Boolean);

            const isSelected = category.tableAreaID === selectedCategory;

            return (
              <Stack
                key={i}
                sx={{
                  minWidth: '100px',
                  height: '80px',
                  p: 1,
                  justifyContent: 'center',
                  alignItems: 'center',

                  borderRadius: 1,
                  border: `1px solid ${borderColor}`,
                  cursor: 'pointer',
                  bgcolor: isSelected ? primary : white,
                  '&:hover': {
                    ...(!isSelected && {
                      backgroundColor: `${secondary}10`,
                      borderColor: `${secondary}80`,
                      color: secondary,
                    }),
                  },
                  '&:hover .card-text': {
                    ...(!isSelected && {
                      color: secondary,
                    }),
                  },
                }}
                onClick={() => handleSelectCategory(category.tableAreaID || '')}
              >
                <Typography
                  className='card-text'
                  sx={{
                    fontSize: '12px',
                    color: isSelected ? '#FFF' : 'default',
                  }}
                >
                  {details}
                </Typography>
                <Typography
                  className='card-text'
                  sx={{
                    fontSize: '12px',
                    color: isSelected ? '#FFF' : textSecondary,
                  }}
                >
                  {number}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        <Divider />
        {/* MAIN */}
        {isLoading || isFetching ? (
          <Stack
            direction='column'
            spacing={2}
            alignItems='center'
            justifyContent='center'
            sx={{ height: '100%', py: 3 }}
          >
            <CircularProgress />
            <Typography variant='body2' color='textSecondary'>
              Loading...
            </Typography>
          </Stack>
        ) : selectedCategory === '' ? (
          <Stack
            direction='column'
            spacing={2}
            alignItems='center'
            justifyContent='center'
            sx={{ height: '100%', py: 3 }}
          >
            Select Table Area
          </Stack>
        ) : (
          <Stack
            sx={{
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              minHeight: 0,
            }}
          >
            {tableList?.length <= 0 && (
              <Stack
                sx={{
                  flex: 1,
                  minHeight: 0,
                  justifyContent: 'center',
                  alignItems: 'center',
                  '#no-overlay-ui': {
                    flex: 1,
                    minHeight: 0,
                  },
                }}
              >
                <NoRowsOverlay details='No Table Found' />
              </Stack>
            )}
            <Grid2 container spacing={2} sx={{ p: 1, m: 0 }}>
              {tableList?.map((item, index) => {
                const isWalkIn = item.transactionDetails?.some((tran) =>
                  tran.guest_type?.toLowerCase()?.includes('walk'),
                );

                const isInHouse = item.transactionDetails?.some((tran) =>
                  tran.guest_type?.toLowerCase()?.includes('house'),
                );

                const isHaveTran = item.transactionDetails?.length !== 0;

                const isDineIn = item.transactionDetails?.some((tran) =>
                  tran.service_option?.toLowerCase()?.includes('dine'),
                );

                const isSelectedTxn = item.transactionDetails?.some(
                  (tran) => tran.transaction_id === selectedTransactionId,
                );

                return (
                  <Grid2 {...{ xs: 6, sm: 3 }} key={index}>
                    <Stack
                      sx={{
                        height: '134px',
                        boxSizing: 'border-box',
                        border: '1px solid transparent',
                        borderRadius: '8px',
                        bgcolor: isInHouse
                          ? `${primary}30`
                          : isWalkIn
                            ? `${secondary}30`
                            : '#EEEEEE',
                        position: 'relative',
                        alignItems: 'center',
                        pt: 2,
                        pb: 1,
                        gap: '4px',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: `${primary}80`,
                        },
                        '&:hover .edit-btn': {
                          display: 'block',
                        },
                        '&:hover .is-selected': {
                          display: 'none',
                        },
                      }}
                      onClick={() => onTableCardClick(item, isHaveTran)}
                    >
                      {isHaveTran && (
                        <Box
                          className='edit-btn'
                          sx={{
                            display: 'none',
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            p: '4px',
                          }}
                        >
                          <IconButton
                            size='small'
                            sx={{
                              bgcolor: lightGray,
                              ':hover': {
                                bgcolor: primary,
                                color: '#FFF',
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditClick(item);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Box>
                      )}
                      {isSelectedTxn && (
                        <Box
                          className='is-selected'
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            p: '4px',
                          }}
                        >
                          <IconButton
                            size='small'
                            sx={{
                              bgcolor: '#E7E7EB',
                              color: primary,
                            }}
                          >
                            <PersonPin />
                          </IconButton>
                        </Box>
                      )}
                      <Chip
                        label={item.tableAcr}
                        sx={{
                          px: 1,
                          height: '26px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          bgcolor: isInHouse
                            ? primary
                            : isWalkIn
                              ? secondary
                              : '',
                          color: isInHouse || isWalkIn ? '#FFF' : '',
                          ':hover': {
                            bgcolor: isInHouse
                              ? primary
                              : isWalkIn
                                ? secondary
                                : borderColor,
                          },
                        }}
                      />

                      {isHaveTran && (
                        <Typography sx={{ fontSize: '12px' }}>
                          {item?.transactionDetails[0]?.guest_name?.trim() ||
                            'No Guest Name'}
                        </Typography>
                      )}

                      <Stack direction='row' gap={1}>
                        {isHaveTran &&
                          item.transactionDetails[0]?.service_option && (
                            <Chip
                              label={item.transactionDetails[0]?.service_option}
                              sx={{
                                height: '26px',
                                cursor: 'default',
                                fontSize: '12px',
                                bgcolor: isDineIn ? success : danger,
                                color: '#FFF',
                                ':hover': {
                                  bgcolor: isDineIn ? success : danger,
                                },
                              }}
                            />
                          )}

                        {isHaveTran && (
                          <Chip
                            label={item?.transactionDetails[0]?.pax + ' Pax'}
                            sx={{
                              height: '26px',
                              cursor: 'default',
                              fontSize: '12px',
                              bgcolor: info,
                              color: '#FFF',
                              ':hover': { bgcolor: info },
                            }}
                          />
                        )}
                      </Stack>
                      <Box sx={{ flexGrow: 1 }} />
                      <Stack>
                        <Typography sx={{ fontSize: '12px' }}>
                          {isHaveTran
                            ? item.transactionDetails[0]?.time
                            : '-- : --'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Grid2>
                );
              })}
            </Grid2>
          </Stack>
        )}
      </DrawerWrapper>

      <ConfirmationDialog
        open={showDialog}
        title={isForTransferTable ? 'Transfer Table' : 'Create Transaction'}
        desc={
          isForTransferTable ? (
            <Stack gap={2} sx={{ width: { xs: '100%', md: 400 } }}>
              <Typography sx={{ fontSize: '16px', color: '#212B36' }}>
                Please confirm these details before proceeding.
              </Typography>
              <Stack
                direction='row'
                sx={{ alignItems: 'center', justifyContent: 'center' }}
                gap={3}
              >
                <Stack sx={{ alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#637381' }}>
                    Source Table
                  </Typography>
                  <Typography
                    sx={{ fontSize: '22px', color: '#212B36', fontWeight: 600 }}
                  >
                    {tableDetails?.tableAcr}
                  </Typography>
                </Stack>
                <ArrowForward
                  sx={{ fontSize: '24px', color: 'primary.main' }}
                />
                <Stack sx={{ alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#637381' }}>
                    Destination Table
                  </Typography>
                  <Typography
                    sx={{ fontSize: '22px', color: '#212B36', fontWeight: 600 }}
                  >
                    {initialTable?.tableAcr}
                  </Typography>
                </Stack>
              </Stack>
              {(isHaveOrders || orders.length > 0) && (
                <Alert
                  severity='warning'
                  sx={{
                    color: 'secondary.main',
                    fontSize: '14px',
                    flex: 1,
                    py: 0,
                  }}
                >
                  The destination table already has active orders. <br />
                  Do you want to proceed with this transfer?
                </Alert>
              )}
            </Stack>
          ) : (
            'Would you like to create a new table transaction?'
          )
        }
        onConfirm={onDialogConfirm}
        onClose={onDialogClose}
        confirmButtonTitle={isForTransferTable ? 'Proceed' : 'Yes'}
        cancelButtonTitle='Cancel'
      />

      <POSConfirmationDialog
        {...{
          dialogDisclosure,
          onConfirm: async () => {
            if (!initialTable) return;
            dispatch(setSelectedTable(initialTable));
            !isForSetTable
              ? tableDetailsDrawerDisclosure.onOpen()
              : handleDrawerClose();

            dialogDisclosure.onClose();
            setTimeout(() => {
              setIsForSetTable(false);
            }, 100);
          },
          desc: 'Are you sure you want to leave this table? Unsaved data will be lost.',
        }}
      />
    </>
  );
};

export default POSSelectTableAreaDrawer;
